
import { Component, Vue, Prop, Watch } from 'vue-property-decorator';
import { SupplierList } from '@/collections/suppliers';
import { SupplierModel } from '@/models/supplier';
import { EventBus } from '@/main';
import { contextSyncService } from '@/services/contextSync';

/** Payload for supplier selection events */
interface SupplierSelectionPayload {
  supplierId: string | number;
  supplierName: string;
  sessionId?: string;
}

/** Action parameters for supplier list filtering */
interface SuppliersActionParameters {
  page?: number;
  limit?: number;
  search?: string;
}

/** Chat response structure for suppliers display */
interface SuppliersChatResponse {
  context?: {
    session_id?: string;
  };
  messages?: Array<{ content: string }>;
}

/** Table pagination and sorting options */
interface TableOptions {
  page: number;
  itemsPerPage: number;
  sortBy?: string[];
  sortDesc?: boolean[];
}

@Component({
  name: 'SuppliersListDisplay'
})
export default class SuppliersListDisplay extends Vue {
  @Prop({ default: null }) readonly chatResponse!: SuppliersChatResponse | null;
  @Prop({ default: (): SuppliersActionParameters => ({}) }) readonly actionParameters!: SuppliersActionParameters;

  private suppliers = new SupplierList();
  private supplierItems: SupplierModel[] = [];
  private loading = false;
  private searchQuery = '';
  private totalSuppliers = 0;
  private isInitialized = false;
  private fetchRequestId = 0;
  private tableOptions: TableOptions = {
    page: 1,
    itemsPerPage: 10
  };

  private headers = [
    { text: 'Name', value: 'name', width: '200px' },
    { text: 'Status', value: 'status', width: '120px' },
    { text: 'Contact', value: 'contactEmail', width: '180px' },
    { text: 'Products', value: 'productCount', width: '120px' },
    { text: 'Lead Time', value: 'leadTime', width: '100px' },
    { text: 'Actions', value: 'actions', sortable: false, width: '180px' }
  ];

  async mounted() {
    // Initialize from action parameters if provided
    if (this.actionParameters) {
      if (this.actionParameters.page) {
        this.tableOptions.page = this.actionParameters.page;
      }
      if (this.actionParameters.limit) {
        this.tableOptions.itemsPerPage = this.actionParameters.limit;
      }
      if (this.actionParameters.search) {
        this.searchQuery = this.actionParameters.search;
      }
    }
    
    await this.fetchSuppliers();
    this.isInitialized = true;
  }

  /**
   * Watch tableOptions for pagination/sort changes and refetch data
   * Skip if not yet initialized to prevent double-fetch on mount
   */
  @Watch('tableOptions', { deep: true })
  private onTableOptionsChange(): void {
    if (!this.isInitialized) {
      return;
    }
    this.fetchSuppliers();
  }

  private async fetchSuppliers() {
    this.fetchRequestId++;
    const currentRequestId = this.fetchRequestId;

    this.loading = true;
    const { page, itemsPerPage } = this.tableOptions;

    try {
      const data = await this.suppliers.fetch({ page, limit: itemsPerPage });

      // Ignore stale responses from older requests
      if (currentRequestId !== this.fetchRequestId) {
        return;
      }

      // Assign a shallow copy to avoid reference issues with stale requests
      this.supplierItems = [...this.suppliers.items];
      this.totalSuppliers = data?.pagination?.total || 0;

      // Sync suppliers to backend context API
      this.syncSuppliersToContext();

    } catch (error) {
      // Ignore errors from stale requests
      if (currentRequestId !== this.fetchRequestId) {
        return;
      }

      EventBus.$emit('show-snackbar', {
        message: 'Failed to load suppliers',
        color: 'error'
      });
    } finally {
      // Only update loading state if this is still the current request
      if (currentRequestId === this.fetchRequestId) {
        this.loading = false;
      }
    }
  }

  get filteredSuppliers(): SupplierModel[] {
    let suppliers = this.supplierItems;

    if (this.searchQuery) {
      const query = this.searchQuery.toLowerCase();
      suppliers = suppliers.filter(s => {
        const name = (s.name ?? '').toLowerCase();
        const email = (s.contactEmail ?? '').toLowerCase();
        return name.includes(query) || email.includes(query);
      });
    }

    return suppliers;
  }

  private getStatusColor(status: string): string {
    switch (status) {
      case 'ACTIVE':
        return 'success';
      case 'INACTIVE':
        return 'grey';
      default:
        return 'error';
    }
  }

  private async selectSupplier(supplier: SupplierModel) {
    // Update context in backend context API
    if (contextSyncService.getSessionId()) {
      const supplierData = {
        id: supplier.id,
        name: supplier.name,
        status: supplier.status,
        contactEmail: supplier.contactEmail,
        productCount: supplier.productCount,
        leadTime: supplier.leadTime,
      };

      // Sync selected supplier to selection context
      await contextSyncService.updateSelectionContext(
        'supplier',
        [supplier.id],
        [supplierData]
      );

      // IMPORTANT: Also sync screen context with supplier info
      // This ensures the agent can read supplier_id from screen_params
      // when the message is sent to show the buying table
      contextSyncService.updateScreenContext({
        screen_name: 'buying_table',
        screen_path: '/purchase-assistant',
        screen_params: {
          supplier_id: supplier.id,
          supplier_name: supplier.name,
        },
        component_state: {
          screen_type: 'buying_table',
          screen_data: {
            supplier_id: supplier.id,
            supplier_name: supplier.name,
          },
        },
      });
    }

    // Emit event to notify chatbox about supplier selection
    // This will send a message to the agent to show the buying table
    const payload: SupplierSelectionPayload = {
      supplierId: supplier.id,
      supplierName: supplier.name,
      sessionId: this.chatResponse?.context?.session_id
    };
    EventBus.$emit('po-builder:supplier-selected', payload);

    // Show confirmation
    EventBus.$emit('show-snackbar', {
      message: `Loading buying table for ${supplier.name}...`,
      color: 'info'
    });
  }

  /**
   * Sync supplier list to backend context API
   * This allows the PO agent to access available suppliers
   */
  private syncSuppliersToContext(): void {
    if (!contextSyncService.getSessionId()) {
      return; // No session set yet
    }

    if (this.supplierItems.length === 0) {
      return; // No suppliers to sync
    }

    // Transform supplier models to context-friendly format
    const suppliersData = this.supplierItems.map(s => ({
      id: s.id,
      name: s.name,
      status: s.status,
      contactEmail: s.contactEmail,
      productCount: s.productCount,
      leadTime: s.leadTime,
    }));

    contextSyncService.updateDataContext('suppliers', suppliersData, {
      totalCount: this.totalSuppliers,
    });

    // Also sync screen context for automatic context sync
    this.syncScreenContext();
  }

  /**
   * Sync screen context to contextSyncService for automatic context attachment.
   * Called after data loads so chatbox can include screen info in messages.
   */
  private syncScreenContext(): void {
    if (!contextSyncService.getSessionId()) {
      return;
    }

    contextSyncService.updateScreenContext({
      screen_name: 'suppliers_list',
      screen_path: '/purchase-assistant',
      screen_params: {
        search: this.searchQuery || undefined,
        page: this.tableOptions.page,
        limit: this.tableOptions.itemsPerPage,
      },
      component_state: {
        screen_type: 'suppliers_list',
        screen_data: {
          total_suppliers: this.totalSuppliers,
          displayed_count: this.filteredSuppliers.length,
          search_query: this.searchQuery || undefined,
          suppliers: this.filteredSuppliers.slice(0, 20).map(s => ({
            id: s.id,
            name: s.name,
            status: s.status,
            product_count: s.productCount,
            lead_time: s.leadTime,
          })),
        },
      },
    });
  }
}
